import React, { FC, ReactNode, useEffect, useRef, useState, TouchEvent } from 'react';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface VerticalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  height?: string;
  swipeThreshold?: number;
  className?: string;
}

export const VerticalDrawer: FC<VerticalDrawerProps> = ({
  isOpen,
  onClose,
  children,
  height = '50vh',
  swipeThreshold = 100,
  className = '',
}) => {
  const [startY, setStartY] = useState<number | null>(null);
  const [currentY, setCurrentY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(isOpen);

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !startY || !drawerRef.current) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - startY;

    if (deltaY <= 0) return;

    e.preventDefault();
    setCurrentY(deltaY);
    drawerRef.current.style.transition = 'none';
    drawerRef.current.style.transform = `translateY(${deltaY}px)`;
  };

  const handleTouchEnd = () => {
    if (!isDragging || !startY || !currentY || !drawerRef.current) {
      setIsDragging(false);
      setStartY(null);
      setCurrentY(null);
      return;
    }

    drawerRef.current.style.transition = 'transform 0.3s ease-in-out';

    if (currentY > swipeThreshold) {
      drawerRef.current.style.transform = `translateY(100%)`;
      setTimeout(() => {
        onClose();
      }, 300);
    } else {
      drawerRef.current.style.transform = `translateY(0)`;
    }

    setIsDragging(false);
    setStartY(null);
    setCurrentY(null);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setStartY(e.clientY);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !startY || !drawerRef.current) return;

    const deltaY = e.clientY - startY;

    if (deltaY <= 0) return;

    e.preventDefault();
    setCurrentY(deltaY);
    drawerRef.current.style.transition = 'none';
    drawerRef.current.style.transform = `translateY(${deltaY}px)`;
  };

  const handleMouseUp = () => {
    handleTouchEnd();
  };

  const handleMouseLeave = () => {
    if (!isDragging) return;
    handleTouchEnd();
  };

  useEffect(() => {
    if (isOpen || !drawerRef.current) return;

    drawerRef.current.style.transform = '';
  }, [isOpen]);

  if (!isOpen && !isDragging) return null;

  return (
    <>
      <UnstyledButton onClick={onClose}>
        <div {...stylex.props(styles.overlay, isOpen && styles.overlayOpen)} />
      </UnstyledButton>

      <div
        ref={drawerRef}
        className={className}
        {...stylex.props(styles.drawer)}
        style={{
          height: height,
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: isDragging ? 'none' : 'transform 0.3s ease-in-out',
        }}
      >
        <div
          ref={contentRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          {...stylex.props(styles.drawerSwapper)}
        >
          <div {...stylex.props(styles.drawerLine)} />
        </div>

        <div {...stylex.props(styles.content)}>{children}</div>
      </div>
    </>
  );
};
